import { useCallback, useRef, useState } from 'react';

/**
 * useLongPress — hook para detectar pulsación larga (long-press) en touch/mouse.
 *
 * Uso:
 *   const longPress = useLongPress(() => onEditSong(song), { delay: 600 });
 *   <div {...longPress.bindings} className={longPress.isPressing ? 'scale-[0.98]' : ''}>
 *
 * Cancelaciones que NO disparan el callback:
 *  - touchmove con desplazamiento > 8px (scroll del usuario)
 *  - touchend / mouseup antes del delay
 *  - mouseleave del elemento
 *
 * Feedback visual: `isPressing` queda true mientras el usuario mantiene
 * pulsado para que la fila pueda mostrar `scale-[0.98]` u otro efecto.
 */
export const useLongPress = (callback, { delay = 600 } = {}) => {
  const timerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const triggeredRef = useRef(false);
  const [isPressing, setIsPressing] = useState(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPressing(false);
  }, []);

  const start = useCallback(
    (e) => {
      // Si el target es un botón o link, ignorar (el child handler se ocupa)
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'button' || tag === 'a' || tag === 'svg' || tag === 'path' || tag === 'input') {
        return;
      }
      triggeredRef.current = false;
      const point = e.touches?.[0] || e;
      startPosRef.current = { x: point.clientX || 0, y: point.clientY || 0 };
      setIsPressing(true);
      timerRef.current = setTimeout(() => {
        triggeredRef.current = true;
        setIsPressing(false);
        // Vibración háptica iOS/Android (no-op en navegadores que no la soportan)
        try {
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(15);
          }
        } catch {
          /* no vibration support */
        }
        callback?.(e);
      }, delay);
    },
    [callback, delay]
  );

  const move = useCallback(
    (e) => {
      if (!timerRef.current) return;
      const point = e.touches?.[0] || e;
      const dx = Math.abs((point.clientX || 0) - startPosRef.current.x);
      const dy = Math.abs((point.clientY || 0) - startPosRef.current.y);
      if (dx > 8 || dy > 8) {
        clear();
      }
    },
    [clear]
  );

  // Si ya disparamos el long-press, bloqueamos el click sintético posterior
  const onClickCapture = useCallback((e) => {
    if (triggeredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      triggeredRef.current = false;
    }
  }, []);

  return {
    isPressing,
    bindings: {
      onTouchStart: start,
      onTouchMove: move,
      onTouchEnd: clear,
      onTouchCancel: clear,
      onMouseDown: start,
      onMouseMove: move,
      onMouseUp: clear,
      onMouseLeave: clear,
      onClickCapture,
    },
  };
};

export default useLongPress;
